import {Component, OnInit} from '@angular/core';
import {Procedure} from '../_models/procedure.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {Options} from 'select2';
import {ProceduresService} from '../_services/procedures.service';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {AttachmentsService} from '../../../shared/attachments/_services/attachments.service';


const EMPTY_PROCEDURE: Procedure = {
    title: '',
    categories: [],
    description: '',
    status: 'Draft',
};

@Component({
    selector: 'app-create-edit-procedure',
    templateUrl: './create-edit-procedure.component.html',
    styleUrls: ['./create-edit-procedure.component.scss']
})
export class CreateEditProcedureComponent implements OnInit {

    id: string;
    procedure: Procedure;
    formGroup: FormGroup;
    procedureCategories = [];

    // Select 2
    multipleOptions: Options;

    // Feedback Messages
    formResponse: boolean;
    formSent: boolean;
    successMsg: string;
    errorMsg: string;

    // Text Editor
    config: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: '80px',
        minHeight: '80px',
        placeholder: '',
        translate: 'no',
        defaultParagraphSeparator: 'p',
        defaultFontName: 'Roboto',
        defaultFontSize: '3',
        toolbarHiddenButtons: [
            [
                'undo',
                'redo',
                // 'bold',
                // 'italic',
                // 'underline',
                'strikeThrough',
                'subscript',
                'superscript',
                'justifyLeft',
                'justifyCenter',
                'justifyRight',
                'justifyFull',
                // 'indent',
                // 'outdent',
                // 'insertUnorderedList',
                // 'insertOrderedList',
                'heading',
                'fontName'
            ],
            [
                // 'fontSize',
                'textColor',
                'backgroundColor',
                'customClasses',
                // 'link',
                // 'unlink',
                'insertImage',
                'insertVideo',
                'insertHorizontalRule',
                // 'removeFormat',
                'toggleEditorMode'
            ]
        ]
    };


    // Subscriptions
    subscriptions = [];

    constructor(private fb: FormBuilder,
                private procedureService: ProceduresService,
                private router: Router,
                private route: ActivatedRoute,
                private fileService: AttachmentsService) {
    }

    ngOnInit(): void {
        // Select 2
        this.multipleOptions = {
            width: '100%',
            multiple: true,
            tags: true,
            placeholder: 'Insert Categories as tags.',
            allowClear: true,
            maximumInputLength: 10,
            maximumSelectionLength: 5
        };

        this.loadProcedure();
    }


    loadProcedure() {
        const sb = this.route.paramMap.pipe(
            switchMap(params => {
                // get id from URL
                this.id = params.get('id');
                if (this.id) {
                    return this.procedureService.getProcedureById(this.id);
                }
                return of(EMPTY_PROCEDURE);
            }),
            catchError(() => {
                return of(undefined);
            }),
        ).subscribe((res: Procedure) => {
            if (!res) {
                this.router.navigate(['/procedures/add'], {relativeTo: this.route}).then(() => {
                });
            }

            this.procedure = res;
            this.loadForm();
        });

        // Add request to unsubscribe list
        this.subscriptions.push(sb);
    }

    loadForm() {

        const procedureData = this.procedure;

        if (!this.procedure) {
            return;
        }

        this.formGroup = this.fb.group({
            title: [procedureData.title, Validators.required],
            categories: [procedureData.categories, Validators.required],
            description: [procedureData.description],
            status: [procedureData.status],
            files: [procedureData.files],
        });

        this.procedureCategories = procedureData.categories;

    }

    // Create/Edit
    save() {
        // Mark all inputs as touched
        this.formGroup.markAllAsTouched();


        // Check if the form is valid
        if (!this.formGroup.valid) {
            // Message Feedback
            this.feedbackMsg('error', 'One or more required fields are not valid!');
            return;
        }

        this.procedure = this.formGroup.value;

        // Check if you are on edit or create
        if (this.id) {
            this.edit();
        } else {
            this.create();
        }
    }

    edit() {
        const updateProcedure = this.procedureService.updateProcedure(this.id, this.procedure).subscribe(() => {

            this.router.navigate(['/procedures/list']);

        }, error => {
            if (error.error.reason === 'TheFieldIsRequired') {
                this.feedbackMsg('error', 'One or more required fields are not valid!');
            } else {
                
                this.feedbackMsg('error', 'Something is wrong! Please contact the support team!');
            }
        });

        // Add request to unsubscribe list
        this.subscriptions.push(updateProcedure);
    }

    create() {
        const createProcedure = this.procedureService.createProcedure(this.procedure).subscribe((response: any) => {
            // Upload files
            this.fileService.uploadFiles('procedure', response.reason).subscribe({
                next(res) {
                    this.router.navigate(['/procedures/list']);
                }
            });

            if (this.fileService.selectedFiles.length === 0) {
                this.router.navigate(['/procedures/list']);
            }

        }, error => {
            if (error.error.reason === 'TheFieldIsRequired') {
                this.feedbackMsg('error', 'One or more required fields are not valid!');
            } else {
                
                this.feedbackMsg('error', 'Something is wrong! Please contact the support team!');
            }
        });

        // Add request to unsubscribe list
        this.subscriptions.push(createProcedure);
    }


    // Scroll Top
    scrollTop() {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

    // FeedBack Msg
    feedbackMsg(type, message) {
        this.scrollTop();

        if (type === 'success') {
            this.formSent = true;
            this.formResponse = true;
            this.successMsg = message;

            setTimeout(() => {
                this.formSent = false;
            }, 4000);

        } else if (type === 'error') {
            this.formSent = true;
            this.formResponse = false;
            this.errorMsg = message;

            setTimeout(() => {
                this.formSent = false;
            }, 4000);
        }
    }


    // helpers for View
    isControlValid(controlName: string): boolean {
        const control = this.formGroup.controls[controlName];
        return control.valid && (control.dirty || control.touched);
    }

    isControlInvalid(controlName: string): boolean {
        const control = this.formGroup.controls[controlName];
        return control.invalid && (control.dirty || control.touched);
    }

    controlHasError(validation: string, controlName: string) {
        const control = this.formGroup.controls[controlName];
        return control.hasError(validation) && (control.dirty || control.touched);
    }

    isControlTouched(controlName: string): boolean {
        const control = this.formGroup.controls[controlName];
        return control.dirty || control.touched;
    }
}
